import { useState } from "react";
import { toast } from "sonner";
import { Upload, FileSpreadsheet, Download } from "lucide-react";
import api, { formatApiError } from "../lib/api";
import { Button } from "./ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "./ui/dialog";

export function ImportExcel({ title, endpoint, templateEndpoint, templateName, columns, onDone, testid }) {
  const [open, setOpen] = useState(false);
  const [file, setFile] = useState(null);
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);

  const downloadTemplate = async () => {
    try {
      const res = await api.get(templateEndpoint, { responseType: "blob" });
      const url = URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement("a");
      a.href = url;
      a.download = templateName;
      a.click();
      URL.revokeObjectURL(url);
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const upload = async () => {
    if (!file) { toast.error("Selecciona un archivo Excel"); return; }
    setBusy(true);
    setResult(null);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const r = await api.post(endpoint, fd, { headers: { "Content-Type": "multipart/form-data" } });
      setResult(r.data);
      toast.success(`${r.data.created} registros importados`);
      onDone?.();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      <Button variant="outline" data-testid={`${testid}-import-button`} onClick={() => { setOpen(true); setResult(null); setFile(null); }}>
        <Upload className="w-4 h-4 mr-1" /> Importar Excel
      </Button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-md" data-testid={`${testid}-import-modal`}>
          <DialogHeader><DialogTitle>{title}</DialogTitle></DialogHeader>
          <div className="space-y-4 py-2">
            <div className="bg-sky-50 border border-sky-200 rounded-lg p-3 text-xs text-slate-700">
              <p className="font-semibold mb-1 flex items-center gap-1"><FileSpreadsheet className="w-4 h-4" /> Columnas de la plantilla:</p>
              <p className="font-mono break-all">{columns.join(", ")}</p>
            </div>
            <Button variant="outline" size="sm" data-testid={`${testid}-template-button`} onClick={downloadTemplate}>
              <Download className="w-4 h-4 mr-1" /> Descargar plantilla
            </Button>
            <input
              type="file"
              accept=".xlsx,.xls"
              data-testid={`${testid}-file-input`}
              className="block w-full text-sm text-slate-600 file:mr-3 file:py-2 file:px-4 file:rounded-lg file:border-0 file:bg-blue-800 file:text-white file:text-sm file:font-medium hover:file:bg-blue-700"
              onChange={(e) => setFile(e.target.files?.[0] || null)}
            />
            {result && (
              <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-3 text-sm" data-testid={`${testid}-import-result`}>
                <p className="font-semibold text-emerald-800">{result.created} registros creados</p>
                {result.errors?.length > 0 && (
                  <ul className="mt-1 text-xs text-rose-600 list-disc pl-4">
                    {result.errors.slice(0, 10).map((e, i) => <li key={`${i}-${e}`}>{e}</li>)}
                    {result.errors.length > 10 && <li>…y {result.errors.length - 10} más</li>}
                  </ul>
                )}
              </div>
            )}
          </div>
          <div className="flex justify-end gap-2">
            <Button variant="outline" data-testid={`${testid}-import-cancel`} onClick={() => setOpen(false)}>Cerrar</Button>
            <Button data-testid={`${testid}-import-submit`} className="bg-blue-800 hover:bg-blue-700" disabled={busy} onClick={upload}>
              {busy ? "Importando…" : "Importar"}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}
